import React, { useState, useEffect, useRef } from 'react';
import authApi, { CommunityScreenerResponse, CreateScreenerRequest, TutorialResponse, BacktestSummary } from '../../../services/authApi';
import { toast } from 'react-toastify';
import '../tool-page.css';
import '../AdminTutorialsPage.css';

interface ScreenerForm {
  name: string;
  description: string;
  tags: string;
  timeframe: string;
  chartinkScreenerId: string;
  orderIndex: number;
  isFree: boolean;
  relatedTutorialIds: number[];
  backtestWinRate: string;
  backtestTotalTrades: string;
  backtestAvgReturn: string;
  backtestMaxDrawdown: string;
  backtestPeriod: string;
  backtestFileUrl: string;
  pineScript: string;
  screenerPrompt: string;
  screenerPromptDesc: string;
}

const BLANK: ScreenerForm = {
  name: '', description: '', tags: '', timeframe: 'Daily',
  chartinkScreenerId: '', orderIndex: 0, isFree: false, relatedTutorialIds: [],
  backtestWinRate: '', backtestTotalTrades: '', backtestAvgReturn: '',
  backtestMaxDrawdown: '', backtestPeriod: '', backtestFileUrl: '',
  pineScript: '', screenerPrompt: '',
  screenerPromptDesc: 'Use this prompt to manually filter stocks or apply criteria in your screener tool.',
};

const TIMEFRAMES = ['Daily', 'Weekly', 'Intraday', 'Monthly'];

const AdminScreenersTab: React.FC = () => {
  const [screeners, setScreeners] = useState<CommunityScreenerResponse[]>([]);
  const [loading, setLoading]     = useState(true);
  const [tutorials, setTutorials] = useState<TutorialResponse[]>([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]     = useState<CommunityScreenerResponse | null>(null);
  const [form, setForm]           = useState<ScreenerForm>(BLANK);
  const [saving, setSaving]       = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    load();
    authApi.adminGetTutorials()
      .then((r) => setTutorials(r.data || []))
      .catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    authApi.adminGetScreeners()
      .then((r) => setScreeners(r.data || []))
      .catch(() => toast.error('Failed to load screeners'))
      .finally(() => setLoading(false));
  };

  const openCreate = () => {
    setEditing(null);
    setForm(BLANK);
    setModalOpen(true);
  };

  const openEdit = (s: CommunityScreenerResponse) => {
    setEditing(s);
    const relatedIds = (s.relatedTutorials || []).map((t) => t.id);
    const bs = s.backtestSummary;
    setForm({
      name: s.name,
      description: s.description || '',
      tags: s.tags.join(', '),
      timeframe: s.timeframe,
      chartinkScreenerId: s.chartinkScreenerId || '',
      orderIndex: s.orderIndex,
      isFree: s.isFree,
      relatedTutorialIds: relatedIds,
      backtestWinRate: bs?.winRate != null ? String(bs.winRate) : '',
      backtestTotalTrades: bs?.totalTrades != null ? String(bs.totalTrades) : '',
      backtestAvgReturn: bs?.avgReturn != null ? String(bs.avgReturn) : '',
      backtestMaxDrawdown: bs?.maxDrawdown != null ? String(bs.maxDrawdown) : '',
      backtestPeriod: bs?.period || '',
      backtestFileUrl: s.backtestFileUrl || '',
      pineScript: s.pineScript || '',
      screenerPrompt: s.screenerPrompt || '',
      screenerPromptDesc: s.screenerPromptDesc || 'Use this prompt to manually filter stocks or apply criteria in your screener tool.',
    });
    setModalOpen(true);
  };

  const closeModal = () => { setModalOpen(false); setEditing(null); setForm(BLANK); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const hasBacktest = form.backtestWinRate || form.backtestTotalTrades;
      const backtestSummary: BacktestSummary | null = hasBacktest ? {
        winRate: form.backtestWinRate ? Number(form.backtestWinRate) : null,
        totalTrades: form.backtestTotalTrades ? Number(form.backtestTotalTrades) : null,
        avgReturn: form.backtestAvgReturn ? Number(form.backtestAvgReturn) : null,
        maxDrawdown: form.backtestMaxDrawdown ? Number(form.backtestMaxDrawdown) : null,
        period: form.backtestPeriod.trim() || null,
      } : null;

      const payload: CreateScreenerRequest = {
        name: form.name.trim(),
        description: form.description.trim(),
        tags: form.tags.trim(),
        timeframe: form.timeframe,
        chartinkScreenerId: form.chartinkScreenerId.trim() || undefined,
        orderIndex: form.orderIndex,
        isFree: form.isFree,
        relatedTutorialIds: form.relatedTutorialIds.join(',') || undefined,
        backtestSummary,
        backtestFileUrl: form.backtestFileUrl.trim() || undefined,
        pineScript: form.pineScript.trim() || undefined,
        screenerPrompt: form.screenerPrompt.trim() || undefined,
        screenerPromptDesc: form.screenerPromptDesc.trim() || undefined,
      };
      if (editing) {
        await authApi.adminUpdateScreener(editing.id, payload);
        toast.success('Screener updated');
      } else {
        await authApi.adminCreateScreener(payload);
        toast.success('Screener created');
      }
      closeModal();
      load();
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (id: number, current: boolean) => {
    try {
      await authApi.adminToggleScreener(id);
      toast.success(current ? 'Screener deactivated' : 'Screener activated');
      setScreeners((prev) => prev.map((s) => s.id === id ? { ...s, isActive: !s.isActive } : s));
    } catch { toast.error('Failed'); }
  };

  const toggleFree = async (id: number, current: boolean) => {
    try {
      await authApi.adminToggleScreenerFree(id);
      toast.success(current ? 'Changed to Pro' : 'Changed to Free');
      setScreeners((prev) => prev.map((s) => s.id === id ? { ...s, isFree: !s.isFree } : s));
    } catch { toast.error('Failed'); }
  };

  const deleteScreener = async (id: number, name: string) => {
    if (!window.confirm(`Delete "${name}"? It will be hidden from all users.`)) return;
    try {
      await authApi.adminDeleteScreener(id);
      setScreeners((prev) => prev.filter((s) => s.id !== id));
      toast.success('Screener deleted');
    } catch { toast.error('Delete failed'); }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const r = await authApi.adminUploadFile(file);
      set('backtestFileUrl', r.data);
      toast.success('File uploaded');
    } catch {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const set = (field: keyof ScreenerForm, value: any) => setForm((f) => ({ ...f, [field]: value }));

  const toggleTutorial = (id: number) => {
    setForm((f) => {
      const has = f.relatedTutorialIds.includes(id);
      return { ...f, relatedTutorialIds: has ? f.relatedTutorialIds.filter((x) => x !== id) : [...f.relatedTutorialIds, id] };
    });
  };

  return (
    <div style={{ padding: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <p className="tool-eyebrow">Content</p>
          <h1 className="tool-title" style={{ marginBottom: 2 }}>Screener Management</h1>
          <p className="tool-subtitle">Manage community screeners linked to ChartInk.</p>
        </div>
        <button className="adm-btn adm-btn-primary" onClick={openCreate}>+ Add Screener</button>
      </div>

      {loading ? (
        <div className="adm-loading">Loading screeners…</div>
      ) : screeners.length === 0 ? (
        <div className="adm-empty">No screeners yet. Add one above.</div>
      ) : (
        <div className="adm-list">
          {screeners.map((s) => (
            <div key={s.id} className="adm-tutorial-row">
              <div className="adm-tut-header">
                <div className="adm-tut-info" style={{ flex: 1 }}>
                  <div className="adm-tut-title">{s.name}</div>
                  <div className="adm-tut-meta">
                    <span className="adm-meta-txt">{s.timeframe}</span>
                    {s.tags.length > 0 && <span className="adm-meta-txt">· {s.tags.join(', ')}</span>}
                    {s.chartinkScreenerId && (
                      <a
                        href={`https://chartink.com/screener/${s.chartinkScreenerId}`}
                        target="_blank" rel="noopener noreferrer"
                        className="adm-meta-txt"
                        style={{ color: 'var(--accent)' }}
                      >
                        · ChartInk ↗
                      </a>
                    )}
                  </div>
                </div>
                <div className="adm-tut-actions">
                  <button
                    className={`adm-status-chip ${s.isActive ? 'adm-chip-active' : 'adm-chip-inactive'}`}
                    onClick={() => toggleActive(s.id, s.isActive)}
                    title={s.isActive ? 'Click to deactivate' : 'Click to activate'}
                  >
                    <span className="adm-chip-dot" />
                    {s.isActive ? 'Active' : 'Inactive'}
                  </button>
                  <button
                    className={`adm-status-chip ${s.isFree ? 'adm-chip-free' : 'adm-chip-pro'}`}
                    onClick={() => toggleFree(s.id, s.isFree)}
                    title={s.isFree ? 'Click to make Pro' : 'Click to make Free'}
                  >
                    {s.isFree ? 'Free' : 'Pro'}
                  </button>
                  <button className="adm-btn adm-btn-sm adm-btn-ghost" onClick={() => openEdit(s)}>Edit</button>
                  <button className="adm-btn adm-btn-sm adm-btn-danger" onClick={() => deleteScreener(s.id, s.name)}>Del</button>
                </div>
              </div>
              {s.description && (
                <div style={{ padding: '0 18px 12px', fontSize: 12.5, color: 'var(--muted)', lineHeight: 1.6 }}>
                  {s.description}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <div className="adm-modal-overlay" onClick={closeModal}>
          <div className="adm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="adm-modal-header">
              <h3>{editing ? 'Edit Screener' : 'New Screener'}</h3>
              <button className="adm-modal-close" onClick={closeModal}>✕</button>
            </div>
            <div className="adm-modal-body">
              <div className="adm-field">
                <label>Name</label>
                <input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Bull Flag Pattern" />
              </div>
              <div className="adm-field">
                <label>Description</label>
                <textarea rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="What this screener does…" />
              </div>
              <div className="adm-field-row">
                <div className="adm-field adm-field--grow">
                  <label>Tags <span className="adm-label-sub">(comma-separated)</span></label>
                  <input value={form.tags} onChange={(e) => set('tags', e.target.value)} placeholder="Breakout, Volume" />
                </div>
                <div className="adm-field adm-field--sm">
                  <label>Timeframe</label>
                  <select
                    value={form.timeframe}
                    onChange={(e) => set('timeframe', e.target.value)}
                    style={{ padding: '9px 12px', border: '1.5px solid var(--border)', borderRadius: 8, background: 'var(--bg)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' }}
                  >
                    {TIMEFRAMES.map((tf) => <option key={tf} value={tf}>{tf}</option>)}
                  </select>
                </div>
              </div>
              <div className="adm-field">
                <label>ChartInk Screener ID <span className="adm-label-sub">(URL slug, optional)</span></label>
                <input value={form.chartinkScreenerId} onChange={(e) => set('chartinkScreenerId', e.target.value)} placeholder="e.g. bull-flag-pattern" />
              </div>
              <div className="adm-field-row">
                <div className="adm-field adm-field--sm">
                  <label>Order</label>
                  <input type="number" value={form.orderIndex} onChange={(e) => set('orderIndex', Number(e.target.value))} />
                </div>
                <div className="adm-field adm-field--grow adm-toggle-row" style={{ alignItems: 'flex-end', paddingBottom: 9 }}>
                  <label className="adm-toggle-label">
                    <input type="checkbox" checked={form.isFree} onChange={(e) => set('isFree', e.target.checked)} />
                    Free (visible without subscription)
                  </label>
                </div>
              </div>
              {/* Backtesting */}
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--muted)', marginBottom: 10 }}>
                  Backtesting Results <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
                </div>
                <div className="adm-field-row">
                  <div className="adm-field adm-field--sm">
                    <label>Win Rate (%)</label>
                    <input type="number" step="0.1" value={form.backtestWinRate} onChange={(e) => set('backtestWinRate', e.target.value)} placeholder="62.5" />
                  </div>
                  <div className="adm-field adm-field--sm">
                    <label>Total Trades</label>
                    <input type="number" value={form.backtestTotalTrades} onChange={(e) => set('backtestTotalTrades', e.target.value)} placeholder="148" />
                  </div>
                  <div className="adm-field adm-field--sm">
                    <label>Avg Return (%)</label>
                    <input type="number" step="0.1" value={form.backtestAvgReturn} onChange={(e) => set('backtestAvgReturn', e.target.value)} placeholder="4.2" />
                  </div>
                  <div className="adm-field adm-field--sm">
                    <label>Max Drawdown (%)</label>
                    <input type="number" step="0.1" value={form.backtestMaxDrawdown} onChange={(e) => set('backtestMaxDrawdown', e.target.value)} placeholder="-8.1" />
                  </div>
                </div>
                <div className="adm-field-row">
                  <div className="adm-field adm-field--grow">
                    <label>Tested Period</label>
                    <input value={form.backtestPeriod} onChange={(e) => set('backtestPeriod', e.target.value)} placeholder="Jan 2023 – Dec 2024" />
                  </div>
                  <div className="adm-field adm-field--grow">
                    <label>Backtest Report File <span className="adm-label-sub">(Excel / PDF — optional)</span></label>
                    <div className="adm-upload-row">
                      <input
                        ref={fileRef}
                        type="file"
                        accept=".xlsx,.xls,.csv,.pdf"
                        onChange={handleFileUpload}
                        className="adm-file-input"
                        disabled={uploading}
                      />
                      {uploading && <span style={{ fontSize: 12, color: 'var(--muted)' }}>Uploading…</span>}
                    </div>
                    {form.backtestFileUrl && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
                        <span style={{ fontSize: 12, color: 'var(--accent)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          ✓ {form.backtestFileUrl.split('/').pop()}
                        </span>
                        <button
                          type="button"
                          className="adm-btn adm-btn-ghost adm-btn-sm"
                          onClick={() => set('backtestFileUrl', '')}
                          style={{ fontSize: 11, padding: '2px 8px' }}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Pine Script */}
              <div className="adm-field">
                <label>Pine Script <span className="adm-label-sub">(TradingView — optional)</span></label>
                <textarea
                  rows={5}
                  value={form.pineScript}
                  onChange={(e) => set('pineScript', e.target.value)}
                  placeholder="//@version=5&#10;indicator('My Screener', overlay=true)&#10;..."
                  style={{ fontFamily: 'monospace', fontSize: 12 }}
                />
              </div>

              {/* Screener Prompt */}
              <div className="adm-field">
                <label>Prompt Description <span className="adm-label-sub">(shown above prompt)</span></label>
                <input
                  value={form.screenerPromptDesc}
                  onChange={(e) => set('screenerPromptDesc', e.target.value)}
                  placeholder="Use this prompt to manually filter stocks..."
                />
              </div>
              <div className="adm-field">
                <label>Screener Prompt <span className="adm-label-sub">(optional)</span></label>
                <textarea
                  rows={4}
                  value={form.screenerPrompt}
                  onChange={(e) => set('screenerPrompt', e.target.value)}
                  placeholder="e.g. Stocks where EMA(20) crosses above EMA(50) with volume > 1.5x average..."
                />
              </div>

              {tutorials.length > 0 && (
                <div className="adm-field">
                  <label>Related Learning <span className="adm-label-sub">(shown on screener detail page)</span></label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflowY: 'auto', padding: '4px 0' }}>
                    {tutorials.map((t) => (
                      <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text)' }}>
                        <input
                          type="checkbox"
                          checked={form.relatedTutorialIds.includes(t.id)}
                          onChange={() => toggleTutorial(t.id)}
                          style={{ accentColor: 'var(--accent)', width: 14, height: 14 }}
                        />
                        <span>{t.badge} {t.title}</span>
                        {t.isFree && <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600 }}>Free</span>}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="adm-modal-footer">
              <button className="adm-btn adm-btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="adm-btn adm-btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminScreenersTab;
